// Meta (Facebook) pixel helpers. The pixel BASE snippet lives in index.html and
// only loads fbevents.js once a real META_PIXEL_ID is pasted in - while the id
// is empty `window.fbq` is never defined and every helper below silently no-ops.
// Same "no key = inert" posture as `trackWeb` in analytics.ts: analytics must
// never throw into the page.

type FbqParams = Record<string, string | number | boolean>

interface FbqOptions {
  eventID: string
}

type Fbq = (
  command: 'track' | 'trackCustom' | 'init',
  eventName: string,
  params?: FbqParams,
  options?: FbqOptions,
) => void

declare global {
  interface Window {
    fbq?: Fbq
  }
}

// First path segments the pixel must never be called on: their paths ARE
// credentials, and fbevents.js sends the full page URL with every event. No SPA
// route lives under them today (Pages Functions serve all three), but this shell
// is the fallback for any path a function does not claim. Mirror of the
// denylist in index.html and public/analytics.js, which cannot import this;
// functions/__tests__/meta-store-click.test.ts runs one table through them all.
const META_PIXEL_DENIED_PAGES = new Set(['trip', 'invite', 'place'])

// A `?token=` query is refused too, as analytics.js and app-banner.js do: no
// SPA route takes one today, but this shell is the fallback for any URL.
export function metaPixelAllowed(pathname: string, search = ''): boolean {
  const segment = pathname.split('/').filter(Boolean)[0] ?? ''
  return !META_PIXEL_DENIED_PAGES.has(segment.toLowerCase()) && !/[?&]token=/i.test(search)
}

function getFbq(): Fbq | undefined {
  if (typeof window === 'undefined') return undefined
  if (!metaPixelAllowed(window.location.pathname, window.location.search ?? '')) return undefined
  return typeof window.fbq === 'function' ? window.fbq : undefined
}

/**
 * A deduplication id shared by the browser pixel event and (later) its
 * server-side Conversions API twin, so Meta counts one conversion, not two.
 */
export function newEventId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // fall through to the non-crypto id below
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}

/** The store listing a click hands off to. */
export type StorePlatform = 'ios' | 'android'

// Owner ruling (2026-10-18): an iOS store click is worth 1.5x an Android one.
// These are RELATIVE optimisation weights for Meta's value-based bidding, not
// money - `currency` is required by Meta for a `value` to be read at all.
// Mirrored in public/analytics.js and public/app-banner.js (which cannot
// import); functions/__tests__/meta-store-click.test.ts asserts they agree.
export const STORE_CLICK_VALUE: Record<StorePlatform, number> = { ios: 1.5, android: 1 }
export const STORE_CLICK_CURRENCY = 'INR'

// A distinct custom event per platform, fired IN ADDITION to `AppStoreClick`,
// so a separate iOS or Android campaign can optimise on its own event without
// a custom-conversion rule.
const PLATFORM_STORE_CLICK_EVENT: Record<StorePlatform, string> = {
  ios: 'AppStoreClickIOS',
  android: 'AppStoreClickAndroid',
}

/**
 * Store click. This is neither a lead nor a verified install. Fires
 * `AppStoreClick` plus the platform's own event, both with the same eventID
 * (Meta dedupes per event name, so sharing it is safe).
 */
export function trackMetaStoreClick(
  props: { store: StorePlatform; placement: string },
  eventId: string,
): void {
  const fbq = getFbq()
  if (!fbq) return
  const params: FbqParams = {
    content_name: 'app_download',
    ...props,
    platform: props.store,
    value: STORE_CLICK_VALUE[props.store],
    currency: STORE_CLICK_CURRENCY,
  }
  try {
    fbq('trackCustom', 'AppStoreClick', params, { eventID: eventId })
    fbq('trackCustom', PLATFORM_STORE_CLICK_EVENT[props.store], params, { eventID: eventId })
  } catch {
    // swallow - analytics must never break the page
  }
}

/** Manual `PageView` for SPA route changes (index.html fires the initial one). */
export function trackMetaPageView(): void {
  const fbq = getFbq()
  if (!fbq) return
  try {
    fbq('track', 'PageView')
  } catch {
    // swallow - analytics must never break the page
  }
}
